import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { useConnectionStatus } from './useConnectionStatus';
import { useOfflineQueue } from './useOfflineQueue';

const API = `${window.__WR_API_BASE__}/api`;

const sendDraftToServer = async (item) => {
  const endpoint = item.target_endpoint === 'update-draft'
    ? `${API}/history/update-draft`
    : `${API}/history/save-draft`;
  const { id, saved_at, status, last_error, target_endpoint, patient_label, ...payload } = item;
  void id; void saved_at; void status; void last_error; void target_endpoint; void patient_label;
  try {
    const res = await axios.post(endpoint, payload, { withCredentials: true });
    return res?.data?.success === true;
  } catch (e) {
    console.warn('[useOfflineAutoDrain] send failed:', e?.message);
    return false;
  }
};

/**
 * useOfflineAutoDrain
 * --------------------
 * Watches the connection status; when transitioning from offline → online,
 * automatically attempts to drain any pending offline drafts. Throttled to
 * one drain attempt every 30 s to avoid retry storms if the connection is
 * flapping.
 *
 * Render this hook ONCE near the root of the app (App.js).
 */
export const useOfflineAutoDrain = () => {
  const { online } = useConnectionStatus();
  const { queueCount, drainQueue } = useOfflineQueue();
  const lastDrainRef = useRef(0);
  const prevOnlineRef = useRef(online);

  useEffect(() => {
    const justCameOnline = online && !prevOnlineRef.current;
    prevOnlineRef.current = online;
    if (!justCameOnline) return;
    if (queueCount === 0) return;
    const now = Date.now();
    if (now - lastDrainRef.current < 30_000) return;
    lastDrainRef.current = now;
    (async () => {
      const { drained, failed } = await drainQueue(sendDraftToServer);
      if (drained > 0) {
        toast.success(
          `Back online — synced ${drained} queued draft${drained !== 1 ? 's' : ''}`,
          { duration: 4000 }
        );
      }
      if (failed > 0) {
        toast.error(
          `${failed} draft${failed !== 1 ? 's' : ''} couldn't sync. Open the Queue panel to retry.`,
          { duration: 5000 }
        );
      }
    })();
  }, [online, queueCount, drainQueue]);
};

export default useOfflineAutoDrain;
